import { Heart, Compass, Sparkle } from "lucide-react";

const pillars = [
  {
    icon: Heart,
    title: "Human-First",
    text: "We craft content that connects — built on empathy, voice, and real conversations with your audience.",
  },
  {
    icon: Compass,
    title: "Strategy-Led",
    text: "Every reel, caption and campaign is rooted in a clear business goal and measured outcome.",
  },
  {
    icon: Sparkle,
    title: "Creatively Bold",
    text: "We don't chase trends — we set the tone with stories your brand will be remembered for.",
  },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-14 items-center">
        {/* Imagery collage */}
        <div className="lg:col-span-5 relative">
          <div className="relative rounded-[24px] overflow-hidden">
            <img
              src={`${process.env.PUBLIC_URL}/img/about.jpeg`}
              alt="Marketing team strategizing"
              className="w-full h-[520px] object-cover"
              data-testid="about-image-primary"
            />
          </div>
          <div className="absolute -bottom-10 -right-6 w-44 h-56 rounded-2xl overflow-hidden border-4 border-pearl shadow-2xl hidden md:block">
            <img
              src={`${process.env.PUBLIC_URL}/img/about-secondary.jpeg`}
              alt="Creative meeting"
              className="w-full h-full object-cover"
              data-testid="about-image-secondary"
            />
          </div>
          <div className="absolute -top-6 -left-6 bg-lilac text-pearl rounded-2xl px-5 py-4 shadow-xl hidden md:block">
            <div className="font-display text-3xl leading-none">2+</div>
            <div className="text-[0.7rem] uppercase tracking-[0.22em] mt-1 opacity-90">
              Years of Story-craft
            </div>
          </div>
        </div>

        {/* Text */}
        <div className="lg:col-span-7">
          <div className="eyebrow mb-5" data-testid="about-eyebrow">
            — About the Studio
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-[1.05]"
            data-testid="about-heading"
          >
            A studio where{" "}
            <span className="italic text-lilac">strategy</span> meets
            soulful{" "}
            <span className="italic text-blush">storytelling</span>.
          </h2>

          <p
            className="mt-7 text-charcoal/75 text-base md:text-lg max-w-2xl leading-relaxed"
            data-testid="about-paragraph-1"
          >
            At She Means Content (SMC), we are a proud women-owned and POC-led social media agency dedicated to empowering small to medium-sized businesses. Our mission is to cultivate vibrant and engaging social media presences that resonate with audiences and drive meaningful connections. With a deep understanding of the unique challenges faced by our clients, we leverage our expertise to develop tailored strategies that enhance visibility and foster community.
          </p>
          <p
            className="mt-4 text-charcoal/70 text-base max-w-2xl leading-relaxed"
            data-testid="about-paragraph-2"
          >
            At SMC, we believe in the power of authentic storytelling and collaboration, ensuring that every brand we work with not only thrives online but also reflects its true essence. Join us in transforming your social media into a powerful tool for growth and connection!
          </p>

          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.title}
                  className="bg-white/70 backdrop-blur rounded-2xl p-5 border border-charcoal/10"
                  data-testid={`about-pillar-${p.title.toLowerCase().replace(/[^a-z]/g, "-")}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-pearl flex items-center justify-center text-charcoal mb-4">
                    <Icon size={18} />
                  </div>
                  <div className="font-display text-xl text-charcoal">
                    {p.title}
                  </div>
                  <p className="mt-2 text-sm text-charcoal/65 leading-relaxed">
                    {p.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
